/**
 * BrandSectionDivider
 *
 * A subtle orange wave divider used between major homepage sections to
 * reinforce the Neuro Wellness brand (orange #F39300) as a visual separator.
 * Lightweight SVG, no layout shift, respects reduced-motion.
 */
type BrandSectionDividerProps = {
  /** Flip the wave direction for visual variety between sections. */
  flip?: boolean;
  /** Use a filled slate background above the wave (for dark→light transitions). */
  variant?: "wave" | "line";
  className?: string;
};

const BrandSectionDivider = ({
  flip = false,
  variant = "wave",
  className = "",
}: BrandSectionDividerProps) => {
  if (variant === "line") {
    return (
      <div
        className={`relative mx-auto w-full max-w-5xl px-6 ${className}`}
        aria-hidden="true"
      >
        <div className="flex items-center justify-center gap-3 py-2">
          <span className="h-px w-16 bg-border sm:w-24" />
          <span className="h-1.5 w-1.5 rotate-45 bg-accent" />
          <span className="h-px w-16 bg-border sm:w-24" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative w-full overflow-hidden leading-none ${className}`}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 1440 48"
        preserveAspectRatio="none"
        className={`block h-6 w-full sm:h-8 ${flip ? "rotate-180" : ""}`}
        fill="none"
      >
        {/* Soft orange wave — thin, subtle accent */}
        <path
          d="M0 24 C 180 8, 360 40, 540 24 C 720 8, 900 40, 1080 24 C 1260 8, 1380 32, 1440 24"
          stroke="hsl(var(--accent))"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.55"
        />
        {/* Faint echo wave for depth */}
        <path
          d="M0 30 C 180 16, 360 44, 540 30 C 720 16, 900 44, 1080 30 C 1260 16, 1380 38, 1440 30"
          stroke="hsl(var(--accent))"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.2"
        />
      </svg>
    </div>
  );
};

export default BrandSectionDivider;
